import { AppForm } from '../../../../../../../common/forms/app.form';
import { FormControl, Validators } from '@angular/forms';
import { calcDaysBetweenDates } from '../../../../../../../services/helpers';
import { CustomValidator, fromToDate } from '../../../../../../../common/forms/custom.validators';
import { ParcelAppEntityParcelWrite, RoadOccupationStageAppEntityApplicationRead } from '../../../../../../../openapi';

export function toStageFormValue(v: RoadOccupationStageAppEntityApplicationRead): NotificationOfEmergencyStageFormValue {
  return {
    startDate: v?.startDate || null,
    endDate: v?.endDate || null,
    dayQuantity: v?.dayQuantity || 0,

    geometry: v?.geometry || '',

    provideRoadNumberAndName: !!v?.provideRoadNumberAndName,
    city: v?.city || '',

    roadNumber: v?.roadNumber || '',
    roadName: v?.roadName || '',
    street: v?.street || '',

    parcels: v?.parcels || [],
    roadArea: (v?.roadArea || 0).toFixed(2).toString().replace('.', ','),
    sidewalkArea: (v?.sidewalkArea || 0).toFixed(2).toString().replace('.', ','),
    bikePathArea: (v?.bikePathArea || 0).toFixed(2).toString().replace('.', ','),
    roadsideArea: (v?.roadsideArea || 0).toFixed(2).toString().replace('.', ','),
    greeneryArea: (v?.greeneryArea || 0).toFixed(2).toString().replace('.', ','),
    shoulderArea: (v?.shoulderArea || 0).toFixed(2).toString().replace('.', ',')
  };
}

export interface NotificationOfEmergencyStageFormValue {
  startDate: Date | null;
  endDate: Date | null;
  dayQuantity: number;

  geometry: string;

  provideRoadNumberAndName: boolean;
  city: string;

  roadName?: string;
  roadNumber?: string;
  street?: string;

  parcels: ParcelAppEntityParcelWrite[];

  roadArea: string;
  sidewalkArea: string;
  bikePathArea: string;
  roadsideArea: string;
  greeneryArea: string;
  shoulderArea: string;
}

export class NotificationOfEmergencyStageForm extends AppForm {
  constructor() {
    super({
      id: new FormControl('', { nonNullable: true }),
      startDate: new FormControl(null, { validators: [Validators.required, CustomValidator.isValidDateFormat(), CustomValidator.minDate(new Date())] }),
      endDate: new FormControl(null, { validators: [Validators.required, CustomValidator.isValidDateFormat()] }),
      dayQuantity: new FormControl(null, [Validators.required, Validators.maxLength(255)]),

      geometry: new FormControl(null),

      provideRoadNumberAndName: new FormControl(true),
      city: new FormControl(
        '',
        { nonNullable: true, validators: [Validators.maxLength(255), Validators.required] }
      ),

      parcels: new FormControl([], {
        nonNullable: true,
        validators: [
          Validators.minLength(1),
          Validators.required
        ]
      }),

      // jezdnia
      roadArea: new FormControl('', {
        validators: [Validators.maxLength(255)],
        nonNullable: true
      }),
      // chodnik
      sidewalkArea: new FormControl('', {
        validators: [Validators.maxLength(255)],
        nonNullable: true
      }),
      // ścieżka rowerowa
      bikePathArea: new FormControl('', {
        validators: [Validators.maxLength(255)],
        nonNullable: true
      }),
      // Rów przydrożny
      roadsideArea: new FormControl('', {
        validators: [Validators.maxLength(255)],
        nonNullable: true
      }),
      // trawnik
      greeneryArea: new FormControl('', {
        validators: [Validators.maxLength(255)],
        nonNullable: true
      }),
      // Pobocze
      shoulderArea: new FormControl('', {
        validators: [Validators.maxLength(255)],
        nonNullable: true
      })
    }, {
      validators: [
        fromToDate('startDate', 'endDate')
      ]
    });
    this.provideRoadNumberAndNameChange(true);
  }

  public get parcels(): FormControl {
    return this.get('parcels') as FormControl;
  }

  public get geometry(): FormControl {
    return this.get('geometry') as FormControl;
  }

  public get provideRoadNumberAndName(): FormControl {
    return this.get('provideRoadNumberAndName') as FormControl;
  }

  public get city(): FormControl {
    return this.get('city') as FormControl;
  }

  public cityChange(v?: string | null): void {
    this.street?.setValue('');
    this.street?.markAsUntouched();
    this.roadNumber?.setValue('');
    this.roadNumber?.markAsUntouched();
  }

  public provideRoadNumberAndNameChange(v?: boolean): void {
    this.city.setValue('');
    this.city.markAsUntouched();
    if (v) {
      this.addControl('roadNumber', new FormControl('', {
        validators: [Validators.required, Validators.maxLength(255)],
        nonNullable: true
      }));

      this.addControl('roadName', new FormControl({
        disabled: true,
        value: null
      }, [Validators.required, Validators.maxLength(255)]));
      this.removeControl('street');
    } else {
      this.removeControl('roadName');
      this.removeControl('roadNumber');
      this.addControl('street', new FormControl('', {
        nonNullable: true,
        validators: [Validators.required, Validators.maxLength(255)]
      }));
    }
  }

  public get roadName(): FormControl | null {
    return this.get('roadName') as FormControl | null;
  }

  public get roadNumber(): FormControl | null {
    return this.get('roadNumber') as FormControl | null;
  }

  public get street(): FormControl | null {
    return this.get('street') as FormControl | null;
  }

  public get startDate(): FormControl {
    return this.get('startDate') as FormControl;
  }

  public get endDate(): FormControl {
    return this.get('endDate') as FormControl;
  }

  public get dayQuantity(): FormControl {
    return this.get('dayQuantity') as FormControl;
  }

  public startDateChange(v: Date): void {
    if (v && this.endDate.value) {
      this.dayQuantity.setValue(
        calcDaysBetweenDates(new Date(v), new Date(this.endDate.value))
      );
    } else {
      this.dayQuantity.setValue('');
    }
  }

  public endDateChange(v: Date): void {
    if (v && this.startDate.value) {
      this.dayQuantity.setValue(
        calcDaysBetweenDates(new Date(this.startDate.value), new Date(v))
      );
    } else {
      this.dayQuantity.setValue('');
    }
  }

  public override get valid(): boolean {
    return super.valid
      && (
        !!this.get('roadArea')?.value
        || !!this.get('sidewalkArea')?.value
        || !!this.get('bikePathArea')?.value
        || !!this.get('roadsideArea')?.value
        || !!this.get('greeneryArea')?.value
        || !!this.get('shoulderArea')?.value
      );
  }

  public override patchFormValues(value: NotificationOfEmergencyStageFormValue): void {
    this.provideRoadNumberAndNameChange(!!value?.provideRoadNumberAndName);
    super.patchFormValues({
      ...value,
      provideRoadNumberAndName: !!value?.provideRoadNumberAndName
    });
  }

  public get values(): NotificationOfEmergencyStageFormValue {
    return {
      ...this.value,
      startDate: this.value.startDate,
      endDate: this.value.endDate,
      dayQuantity: this.dayQuantity.value,

      geometry: this.geometry.value,

      provideRoadNumberAndName: this.value?.provideRoadNumberAndName,
      city: this.value.city,

      roadName: this.roadName?.value || null,
      roadNumber: this.roadNumber?.value || null,
      street: this.value?.street || null,

      parcels: this.parcels.value,
      bikePathArea: this.value.bikePathArea || null
    };
  }
}
